import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { NotFound } from '@cloudgenie/ui';
import { AuthProvider } from './AuthContext';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DocsPage from './pages/DocsPage';
import StatusPage from './pages/StatusPage';
import BillingPage from './pages/BillingPage';
import AdminPage from './pages/AdminPage';
import AdminTenantPage from './pages/AdminTenantPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminApiKeysPage from './pages/AdminApiKeysPage';
import CliAuthorizePage from './pages/CliAuthorizePage';
import { AdminLayout } from './pages/AdminLayout';

// Legacy admin pages (ported from frontend/src/pages/admin/*).
// Wrapped in Suspense-free direct imports for now; can lazy-load later.
import UsersGroupsPage from './pages/admin/UsersGroups';
import OrganizationPage from './pages/admin/Organization';
import CloudConfigHubPage from './pages/admin/CloudConfigHub';
import PoliciesPage from './pages/admin/Policies';
import SecretBrokersPage from './pages/admin/SecretBrokers';
import SchedulerPage from './pages/admin/Scheduler';
import ModulesPage from './pages/admin/Modules';
import TrialsAdminPage from './pages/admin/TrialsAdmin';
import AddServicePage from './pages/admin/AddService';
import AgentsPage from './pages/admin/Agents';
import PlatformHealthPage from './pages/admin/PlatformHealth';
import NotificationsPage from './pages/admin/Notifications';
import PlatformSettingsPage from './pages/admin/PlatformSettings';
import TestUsersPage from './pages/admin/TestUsers';
import AuditLogPage from './pages/admin/AuditLog';
import MyProfilePage from './pages/admin/MyProfile';

function AboutPage() {
  return (
    <div className="p-8">
      <h1 className="text-2xl font-semibold">About CloudGenie</h1>
      <p className="mt-3 text-muted-foreground">
        CloudGenie Enterprise FinOps &amp; IaC platform. See{' '}
        <a className="text-primary hover:underline" href="https://docs.cloudgenie.co" target="_blank" rel="noreferrer">docs.cloudgenie.co</a>.
      </p>
    </div>
  );
}

/** Wraps a legacy admin element in the shared sidebar layout. */
function A({ children }: { children: React.ReactNode }) {
  return <AdminLayout>{children}</AdminLayout>;
}

export default function App() {
  const isStatusHost = window.location.hostname.startsWith('status.');
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public pages */}
          <Route path="/" element={isStatusHost ? <StatusPage /> : <HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/register" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/:slug" element={<DocsPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/billing" element={<A><BillingPage /></A>} />
          <Route path="/billing/success" element={<A><BillingPage /></A>} />
          <Route path="/cli-authorize" element={<A><CliAuthorizePage /></A>} />

          {/* Admin console (existing) */}
          <Route path="/admin" element={<A><AdminPage /></A>} />
          <Route path="/admin/tenants" element={<A><AdminPage /></A>} />
          <Route path="/admin/tenants/:id" element={<A><AdminTenantPage /></A>} />
          <Route path="/admin/users" element={<A><AdminUsersPage /></A>} />
          <Route path="/admin/api-keys" element={<A><AdminApiKeysPage /></A>} />

          {/* Legacy admin sections (ported) */}
          <Route path="/admin/users-groups" element={<A><UsersGroupsPage /></A>} />
          <Route path="/admin/organization" element={<A><OrganizationPage /></A>} />
          <Route path="/admin/cloud-config" element={<A><CloudConfigHubPage /></A>} />
          <Route path="/admin/policies" element={<A><PoliciesPage /></A>} />
          <Route path="/admin/secret-brokers" element={<A><SecretBrokersPage /></A>} />
          <Route path="/admin/scheduler" element={<A><SchedulerPage /></A>} />
          <Route path="/admin/modules" element={<A><ModulesPage /></A>} />
          <Route path="/admin/trials" element={<A><TrialsAdminPage /></A>} />
          <Route path="/admin/add-service" element={<A><AddServicePage /></A>} />
          <Route path="/admin/agents" element={<A><AgentsPage /></A>} />
          <Route path="/admin/platform-health" element={<A><PlatformHealthPage /></A>} />
          <Route path="/admin/notifications" element={<A><NotificationsPage /></A>} />
          <Route path="/admin/settings" element={<A><PlatformSettingsPage /></A>} />
          <Route path="/admin/test-users" element={<A><TestUsersPage /></A>} />
          <Route path="/admin/audit-log" element={<A><AuditLogPage /></A>} />
          <Route path="/admin/profile" element={<A><MyProfilePage /></A>} />
          <Route path="/admin/about" element={<A><AboutPage /></A>} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
